/**
 * Configuration service for managing character limits
 * 문자 제한 관리를 위한 설정 서비스
 */

import { ConfigLimits } from '@/types/config';
import { apiClient } from '@/api/api-client';

const CONFIG_STORAGE_KEY = 'kpc_config_limits';

export class ConfigService {
  private static instance: ConfigService;
  private configLimits: ConfigLimits | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   * 싱글톤 인스턴스 반환
   */
  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  /**
   * Load configuration limits from API and store in localStorage
   * API에서 설정 제한값을 로드하여 localStorage에 저장
   */
  public async loadConfigLimits(): Promise<ConfigLimits> {
    try {
      console.log('설정 제한값 로드 시작 / Loading config limits...');
      
      // V2 API (Gemini/Imagen) 사용 / Use V2 API (Gemini/Imagen)
      const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'https://ailic.kpc.or.kr';
      const res = await fetch(`${baseUrl}/api/v2/kpc/config/limits`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const configLimits = await res.json() as ConfigLimits;
      
      // Validate the response
      if (!configLimits || 
          typeof configLimits.textMaxPromptLength !== 'number' ||
          typeof configLimits.imageMaxPromptLength !== 'number' ||
          typeof configLimits.translationMaxOutputLength !== 'number') {
        throw new Error('Invalid config limits response');
      }

      // Store in memory and localStorage
      this.configLimits = configLimits;
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(configLimits));
      
      console.log('설정 제한값 로드 완료 / Config limits loaded successfully:', configLimits);
      return configLimits;
    } catch (error) {
      console.error('설정 제한값 로드 실패 / Failed to load config limits:', error);
      
      // Fallback to default values
      const defaultLimits: ConfigLimits = {
        textMaxPromptLength: 3000,
        imageMaxPromptLength: 3000,
        translationMaxOutputLength: 3000,
      };
      
      this.configLimits = defaultLimits;
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(defaultLimits));
      
      return defaultLimits;
    }
  }

  /**
   * Get configuration limits from memory or localStorage
   * 메모리 또는 localStorage에서 설정 제한값 조회
   */
  public getConfigLimits(): ConfigLimits {
    // Return from memory if available
    if (this.configLimits) {
      return this.configLimits;
    }

    // Try to load from localStorage
    try {
      const stored = localStorage.getItem(CONFIG_STORAGE_KEY);
      if (stored) {
        const configLimits = JSON.parse(stored) as ConfigLimits;
        
        // Validate stored data
        if (configLimits && 
            typeof configLimits.textMaxPromptLength === 'number' &&
            typeof configLimits.imageMaxPromptLength === 'number' &&
            typeof configLimits.translationMaxOutputLength === 'number') {
          this.configLimits = configLimits;
          return configLimits;
        }
      }
    } catch (error) {
      console.warn('localStorage에서 설정값 로드 실패 / Failed to load config from localStorage:', error);
    }

    // Return default values
    const defaultLimits: ConfigLimits = {
      textMaxPromptLength: 3000,
      imageMaxPromptLength: 3000,
      translationMaxOutputLength: 3000,
    };
    
    this.configLimits = defaultLimits;
    return defaultLimits;
  }

  /**
   * Clear configuration from memory and localStorage
   * 메모리와 localStorage에서 설정값 제거
   */
  public clearConfig(): void {
    this.configLimits = null;
    localStorage.removeItem(CONFIG_STORAGE_KEY);
  }
}

// Export singleton instance
export const configService = ConfigService.getInstance();
